import React, { useCallback, useEffect, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate, useParams, NavLink, Link } from 'react-router-dom';
import { initAuth, isSignedIn, getStoredAccount, signOut, isEntraConfigured } from './services/auth';
import { fetchProjects, fetchFullProject, fetchTenantContext } from './services/api';
import { getSelectedTenantId } from '@mobile-services/tenantSelection';
import { ProjectProvider } from './ProjectContext';
import { getProjectId, getProjectName } from '@shared/targetHelpers';
import { Loading, ErrorText, errorMessage } from './components/ui';
import logo from '@assets/ydyai-logo.png';

import LoginPage from './pages/LoginPage';
import OrgPickerPage from './pages/OrgPickerPage';
import AccountPage from './pages/AccountPage';
import ProjectSelectPage from './pages/ProjectSelectPage';
import DocumentsPage from './pages/DocumentsPage';
import DocumentDetailPage from './pages/DocumentDetailPage';
import UploadPage from './pages/UploadPage';
import LocationStructurePage from './pages/LocationStructurePage';
import ChatPage from './pages/ChatPage';
import AgentsPage from './pages/AgentsPage';
import AgentEditPage from './pages/AgentEditPage';
import AiConnectionsPage from './pages/AiConnectionsPage';
import ReportsPage from './pages/ReportsPage';
import TeamPage from './pages/TeamPage';
import SettingsPage from './pages/SettingsPage';
import ConnectionsPage from './pages/ConnectionsPage';
import LandingPage from './pages/LandingPage';
import WelcomePage from './pages/WelcomePage';
import AcceptInvitePage, { peekPendingInvite } from './pages/AcceptInvitePage';
import { PrivacyPolicyPage, TermsOfServicePage, CookiePolicyPage } from './pages/LegalPages';
import SupportPage from './pages/SupportPage';
import EmbeddedProcorePage from './pages/EmbeddedProcorePage';

function TopBar({ account }) {
  return (
    <header className="topbar">
      <Link to="/projects" aria-label="Go to projects">
        <img className="logo" src={logo} alt="your data your ai" />
      </Link>
      <div className="topbar-spacer" />
      {account && <span className="topbar-user">{account.name || account.email}</span>}
      {account && <button type="button" className="btn secondary compact" onClick={signOut}>Sign out</button>}
    </header>
  );
}

// Project-scoped tabs. Mirrors the mobile app's split between home-level and
// project-level navigation: everything here needs a project in context.
function ProjectTabs({ projectId }) {
  const tabs = [
    ['', 'Documents'],
    ['upload', 'Upload'],
    ['location', 'Location structure'],
    ['chat', 'Chat'],
    ['reports', 'Reports'],
  ];
  return (
    <nav className="tabs">
      {tabs.map(([path, label]) => (
        <NavLink
          key={path}
          end={path === ''}
          to={`/projects/${encodeURIComponent(projectId)}/${path}`}
          className={({ isActive }) => `tab${isActive ? ' active' : ''}`}
        >
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function AccountTabs() {
  const tabs = [
    ['/projects', 'Projects'],
    ['/agents', 'Agents'],
    ['/ai-connections', 'AI connections'],
    ['/team', 'Team'],
    ['/settings', 'Settings'],
    ['/connections', 'Integrations'],
    ['/account', 'Account'],
  ];
  return (
    <nav className="tabs">
      {tabs.map(([path, label]) => (
        <NavLink key={path} to={path} className={({ isActive }) => `tab${isActive ? ' active' : ''}`}>{label}</NavLink>
      ))}
    </nav>
  );
}

// Loads the project once and provides it to every project-scoped page, the
// same contract the mobile ProjectHomeScreen sets up via ProjectProvider - so
// screens ported from mobile can keep using useProject() unchanged.
//
// A LAYOUT route, not a wrapper component.
//
// Each project tab used to be its own route, each wrapping its page in its own
// <ProjectShell>. Those are different element trees, so React Router unmounted
// and remounted the shell on every tab click - and the shell fetches the whole
// project when it mounts. Documents -> Chat -> Reports -> Documents was four
// full project loads, each one a round trip to a Function App that may have
// gone cold, with "Loading project…" over the page every time.
//
// As a layout route it mounts once per projectId and the tabs swap beneath it
// through <Outlet />, so moving between them costs nothing at all. The refetch
// still happens when the project itself changes, which is the only time the
// data can actually be different.
function ProjectShell() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const refreshProject = useCallback(async () => {
    try {
      const full = await fetchFullProject(projectId);
      setProject(full);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    refreshProject().finally(() => setLoading(false));
  }, [refreshProject]);

  return (
    <ProjectProvider project={project} refreshProject={refreshProject}>
      <ProjectTabs projectId={projectId} />
      <main className="page">
        <Link to="/projects" className="breadcrumb">← All projects</Link>
        <h1>{getProjectName(project) || 'Project'}</h1>
        <ErrorText>{error}</ErrorText>
        {loading ? <Loading text="Loading project…" /> : <Outlet />}
      </main>
    </ProjectProvider>
  );
}

// Viewable by anyone, signed in or not - a policy page must not require an
// account to read, and an invitee is by definition not yet a member of the
// tenant they are being let into, so /accept-invite cannot sit behind the
// membership gate either.
const ALWAYS_PUBLIC = new Set(['/privacy', '/terms', '/cookies', '/support', '/accept-invite']);
// The front door. Shown only to visitors without a session; a signed-in user
// asking for "/" wants their projects, not the price list.
const SIGNED_OUT_ONLY = new Set(['/', '/pricing', '/welcome']);

function currentPath() {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname.replace(/\/+$/, '') || '/';
}

export default function App() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [account, setAccount] = useState(null);
  // Read once at mount, not per render: AcceptInvitePage consumes the token as
  // it starts, and re-reading storage would unmount the page mid-acceptance.
  const [pendingInvite] = useState(peekPendingInvite);
  // Whether this identity holds seats in more than one tenant and has not yet
  // said which. The API derives the tenant from the identity alone, so two
  // active seats with no choice made resolve to NONE, and every call fails
  // tenant_required. Nothing asked the question before - the /org route
  // existed but was unreachable - so gaining a second seat (a new
  // subscription, or a guest invite from another customer) locked the account
  // out of the whole app with no way back in.
  const [needsOrg, setNeedsOrg] = useState(false);

  useEffect(() => {
    initAuth()
      .then(() => setAccount(isSignedIn() ? getStoredAccount() : null))
      .catch(() => setAccount(null))
      .finally(() => setReady(true));
  }, []);

  // Asked once per load, after sign-in. A choice already made this session
  // short-circuits it - the header that choice sets is what makes the tenant
  // resolve in the first place.
  useEffect(() => {
    if (!account) return undefined;
    if (getSelectedTenantId()) { setNeedsOrg(false); return undefined; }
    let live = true;
    fetchTenantContext()
      .then((ctx) => { if (live) setNeedsOrg(ctx?.mode === 'needs_selection'); })
      .catch(() => { /* a transient failure is not a reason to prompt */ });
    return () => { live = false; };
  }, [account]);

  // The public pages must render for a visitor who has not signed in and never
  // will until they have bought something - so they are matched before any of
  // the auth gates below, not inside the signed-in route table.
  const path = currentPath();
  const publicRoutes = (
    <Routes>
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      {/* Apple takes a Support URL as a required field and a reviewer opens
          it, so this has to resolve without signing in. */}
      <Route path="/support" element={<SupportPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
    </Routes>
  );
  // Before the auth check, so a policy page renders instantly and without a
  // session rather than flashing a sign-in spinner at a reader.
  if (ALWAYS_PUBLIC.has(path)) return publicRoutes;

  // The embedded surfaces run before every gate below, because inside an
  // iframe none of them can resolve: a redirect to Entra is blocked, the
  // landing page is meaningless in a 400-px panel, and TopBar's "all projects"
  // chrome has nowhere to go. The page owns its own sign-in and its own
  // layout - see EmbeddedProcorePage for why each of those is forced.
  if (path.startsWith('/embedded/')) {
    return (
      <Routes>
        <Route path="/embedded/procore" element={<EmbeddedProcorePage />} />
        <Route path="*" element={<Navigate to="/embedded/procore" replace />} />
      </Routes>
    );
  }

  if (!ready) return <div className="center-screen"><Loading text="Signing in…" /></div>;

  // Second half of the invite round trip. MSAL returns a signed-in user to "/"
  // rather than the link they clicked, so a parked token means "you were in
  // the middle of accepting an invitation" - resume it before any of the
  // routing below sends them to a workspace they do not have a seat in yet.
  if (pendingInvite) return <AcceptInvitePage />;

  if (!account && SIGNED_OUT_ONLY.has(path)) {
    return (
      <Routes>
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    );
  }
  if (!isEntraConfigured()) {
    return (
      <div className="center-screen">
        <div className="card" style={{ maxWidth: 520 }}>
          <h2>Sign-in isn't configured</h2>
          <p className="muted">
            Set <code>VITE_AAD_CLIENT_ID</code> and <code>VITE_AAD_TENANT_ID</code> for this build.
            They're the same values the mobile app uses.
          </p>
        </div>
      </div>
    );
  }
  if (!account) return <LoginPage />;

  // Before the route table: with no tenant resolved, every page behind this
  // point renders an error rather than content, so asking which organisation
  // is the only useful thing on screen.
  if (needsOrg) {
    return (
      <div className="app">
        <TopBar account={account} />
        <main className="page page-narrow">
          <OrgPickerPage onPicked={() => { setNeedsOrg(false); navigate('/projects'); }} />
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <TopBar account={account} />
      <Routes>
        <Route path="/" element={<Navigate to="/projects" replace />} />
        <Route path="/org" element={<><AccountTabs /><main className="page page-narrow"><OrgPickerPage onPicked={() => navigate('/projects')} /></main></>} />
        <Route path="/projects" element={<><AccountTabs /><main className="page"><ProjectSelectPage /></main></>} />
        <Route path="/agents" element={<><AccountTabs /><main className="page"><AgentsPage /></main></>} />
        <Route path="/agents/new" element={<><AccountTabs /><main className="page page-narrow"><AgentEditPage /></main></>} />
        <Route path="/agents/:agentId" element={<><AccountTabs /><main className="page page-narrow"><AgentEditPage /></main></>} />
        <Route path="/ai-connections" element={<><AccountTabs /><main className="page"><AiConnectionsPage /></main></>} />
        <Route path="/team" element={<><AccountTabs /><main className="page"><TeamPage /></main></>} />
        <Route path="/settings" element={<><AccountTabs /><main className="page"><SettingsPage /></main></>} />
        <Route path="/account" element={<><AccountTabs /><main className="page page-narrow"><AccountPage /></main></>} />
        <Route path="/connections" element={<><AccountTabs /><main className="page"><ConnectionsPage /></main></>} />

        <Route path="/projects/:projectId" element={<ProjectShell />}>
          <Route index element={<DocumentsPage />} />
          <Route path="upload" element={<UploadPage />} />
          <Route path="location" element={<LocationStructurePage />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="documents/:blobName" element={<DocumentDetailPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/projects" replace />} />
      </Routes>
    </div>
  );
}
